import type { Product } from '@micro-apps/stores';
import type {
	AvailabilityContext,
	AvailabilityState,
	ExperienceFeatureCardData,
	ExperienceProductCardData,
	JourneyPhase,
	UserState,
} from './types';

export type ProductAvailabilitySource = {
	allProducts?: Product[];
	fetching?: boolean;
};

export type CurrencySource = {
	preferredCurrency?: string;
	isSignedIn?: boolean;
	isPersistentlySignedIn?: boolean;
	isFullySignedIn?: boolean;
};

export type ExperiencesGridStores = {
	productAvailabilityStore?: ProductAvailabilitySource;
	cartStore?: { bookingForProperty?: unknown };
	bookingsStore?: { activeBooking?: unknown };
	userStore?: CurrencySource;
};

export function availabilityStateFromIconColor(iconColor?: string): AvailabilityState | undefined {
	switch (iconColor) {
		case 'GREEN':
			return 'available';
		case 'YELLOW':
			return 'some';
		case 'RED':
			return 'none';
		default:
			return undefined;
	}
}

export function availabilityContextFromStores({
	cartStore,
	bookingsStore,
	userStore,
}: ExperiencesGridStores): AvailabilityContext | undefined {
	if (!cartStore && !bookingsStore && !userStore) return undefined;

	const activeBooking = bookingsStore?.activeBooking;
	const cartBooking = cartStore?.bookingForProperty;

	// A guest who retrieved a reservation (Find a Reservation) has no profile but has
	// proven booking ownership - the SHC matrix treats them as partially authenticated.
	const userState: UserState = userStore?.isFullySignedIn
		? 'authenticated'
		: userStore?.isPersistentlySignedIn
			? 'persistent'
			: activeBooking
				? 'partially-authenticated'
				: 'unauthenticated';

	const journeyPhase: JourneyPhase = activeBooking
		? 'pre-arrival'
		: cartBooking
			? 'shopping'
			: 'browsing';

	return { userState, journeyPhase, hasStayAtProperty: Boolean(activeBooking || cartBooking) };
}

export function withLiveAvailability<
	T extends ExperienceProductCardData | ExperienceFeatureCardData,
>(card: T, stores: ExperiencesGridStores): T {
	const products = stores.productAvailabilityStore?.allProducts;
	if (!card.productId || !products?.length) return card;

	const product = products.find(candidate => candidate.id === card.productId);
	if (!product) return card;

	const state = availabilityStateFromIconColor(product.availabilitySummary?.iconColor);
	if (!state) return card;

	return { ...card, availability: { state } };
}
